import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 60, left: 60 };
const CHART_HEIGHT = 320;

const BAR_COLORS = {
  start: '#6b7280',
  increase: '#16a34a',
  decrease: '#dc2626',
  neutral: '#d1d5db',
  total: '#2563eb',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function WaterfallChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Scales
    const x = d3.scaleBand()
      .domain(data.map((d) => d.label))
      .range([0, innerW])
      .padding(0.4);

    // For y-axis: find range of values that floating bars span
    // start/total bars go from 0, but we want detail on deltas
    const allBarTops = [];
    const allBarBottoms = [];

    data.forEach((d, i) => {
      if (d.type === 'start' || d.type === 'total') {
        allBarTops.push(d.cumulative);
        allBarBottoms.push(0);
      } else if (d.type === 'increase') {
        const prevCumulative = i > 0 ? data[i - 1].cumulative : 0;
        allBarTops.push(d.cumulative);
        allBarBottoms.push(prevCumulative);
      } else if (d.type === 'decrease') {
        const prevCumulative = i > 0 ? data[i - 1].cumulative : 0;
        allBarTops.push(prevCumulative);
        allBarBottoms.push(d.cumulative);
      } else {
        // neutral
        allBarTops.push(d.cumulative);
        allBarBottoms.push(d.cumulative);
      }
    });

    const yMin = Math.min(0, d3.min(allBarBottoms));
    const yMax = d3.max(allBarTops);
    const yPadding = (yMax - yMin) * 0.12 || 1;

    const y = d3.scaleLinear()
      .domain([yMin, yMax + yPadding])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks();
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.5em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(6).tickFormat((v) => `$${v}`))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    const barWidth = x.bandwidth() * 0.6;
    const barOffset = (x.bandwidth() - barWidth) / 2;

    // Draw connector lines and bars
    data.forEach((d, i) => {
      const barX = x(d.label) + barOffset;
      let barTop, barBottom, barHeight, color;

      if (d.type === 'start') {
        barTop = y(d.cumulative);
        barBottom = y(0);
        barHeight = Math.max(1, barBottom - barTop);
        color = BAR_COLORS.start;
      } else if (d.type === 'total') {
        barTop = y(d.cumulative);
        barBottom = y(0);
        barHeight = Math.max(1, barBottom - barTop);
        color = BAR_COLORS.total;
      } else if (d.type === 'increase') {
        const prevCumulative = i > 0 ? data[i - 1].cumulative : 0;
        barTop = y(d.cumulative);
        barBottom = y(prevCumulative);
        barHeight = Math.max(1, barBottom - barTop);
        color = BAR_COLORS.increase;
      } else if (d.type === 'decrease') {
        const prevCumulative = i > 0 ? data[i - 1].cumulative : 0;
        barTop = y(d.cumulative);
        barBottom = y(prevCumulative);
        barHeight = Math.max(1, barBottom - barTop);
        color = BAR_COLORS.decrease;
      } else {
        // neutral
        barTop = y(d.cumulative) - 0.5;
        barBottom = y(d.cumulative) + 0.5;
        barHeight = 1;
        color = BAR_COLORS.neutral;
      }

      // Connector line from previous bar to this bar
      if (i > 0 && d.type !== 'total') {
        const prevD = data[i - 1];
        const prevBarX = x(prevD.label) + barOffset + barWidth;
        const connectorY = y(prevD.cumulative);

        g.append('line')
          .attr('x1', prevBarX)
          .attr('x2', barX)
          .attr('y1', connectorY)
          .attr('y2', connectorY)
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '3,2');
      }

      // Bar rect
      g.append('rect')
        .attr('x', barX)
        .attr('y', Math.min(barTop, barBottom))
        .attr('width', barWidth)
        .attr('height', barHeight)
        .attr('fill', color)
        .attr('rx', 2);

      // Value label
      let labelText, labelColor, labelY;

      if (d.type === 'start' || d.type === 'total') {
        labelText = `$${d.cumulative.toFixed(2)}`;
        labelColor = d.type === 'start' ? BAR_COLORS.start : BAR_COLORS.total;
        labelY = barTop - 6;
      } else if (d.type === 'increase') {
        labelText = `+$${d.delta.toFixed(2)}`;
        labelColor = BAR_COLORS.increase;
        labelY = barTop - 6;
      } else if (d.type === 'decrease') {
        labelText = `-$${Math.abs(d.delta).toFixed(2)}`;
        labelColor = BAR_COLORS.decrease;
        labelY = barBottom + 14;
      } else {
        // neutral - no label shown
        labelText = null;
      }

      if (labelText) {
        g.append('text')
          .attr('x', barX + barWidth / 2)
          .attr('y', labelY)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', 600)
          .attr('fill', labelColor)
          .text(labelText);
      }

      // Invisible hover target
      g.append('rect')
        .attr('x', x(d.label))
        .attr('y', 0)
        .attr('width', x.bandwidth())
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          const prevCumulative = i > 0 ? data[i - 1].cumulative : null;
          const pctChange = prevCumulative && prevCumulative !== 0
            ? ((d.cumulative - prevCumulative) / prevCumulative) * 100
            : null;

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date: d.date,
            avgPrice: d.avgPrice,
            delta: d.delta,
            pctChange,
            type: d.type,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Cumulative Price Changes</div>
          <div style={styles.subtitle}>Step-by-step average price evolution between scrape dates</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No waterfall data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Cumulative Price Changes</div>
        <div style={styles.subtitle}>Step-by-step average price evolution between scrape dates</div>
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {(() => {
              const date = new Date(tooltip.date);
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
            })()}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Avg Price</span>
            <span style={{ fontWeight: 600 }}>${tooltip.avgPrice.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Delta</span>
            <span
              style={{
                fontWeight: 600,
                color: tooltip.delta > 0 ? '#16a34a' : tooltip.delta < 0 ? '#dc2626' : '#6b7280',
              }}
            >
              {tooltip.delta > 0 ? '+' : ''}${tooltip.delta.toFixed(2)}
            </span>
          </div>
          {tooltip.pctChange !== null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>% Change</span>
              <span
                style={{
                  fontWeight: 600,
                  color: tooltip.pctChange > 0 ? '#16a34a' : tooltip.pctChange < 0 ? '#dc2626' : '#6b7280',
                }}
              >
                {tooltip.pctChange > 0 ? '+' : ''}{tooltip.pctChange.toFixed(2)}%
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
